<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useOrderPayments } from "@/composables";
import { OrderPaymentsVariant, OrderStatus } from "@/constants/order";
import { getEntityKey, isSameEntity } from "@/utils/sameEntity";
import OrderPaymentItem from "@/components/order/OrderPayment.vue";
import AddOrderPaymentButton from "@/components/buttons/AddOrderPaymentButton.vue";
import DeletedChip from "@/components/chips/DeletedChip.vue";
import OrderPaymentStatus from "@/components/statuses/OrderPaymentStatus.vue";
import type { OrderPayment } from "@/api/orders/dto/order.dto";

const payments = defineModel<OrderPayment[]>({ default: () => [] });

const props = withDefaults(
  defineProps<{
    orderStatus?: OrderStatus;
    canRestore?: boolean;
    readonly?: boolean;
    variant?: OrderPaymentsVariant;
  }>(),
  {
    orderStatus: undefined,
    canRestore: false,
    readonly: false,
    variant: OrderPaymentsVariant.Default,
  },
);

const { t } = useI18n();
const { createOrderPaymentDraft, getMandatoryOrderPaymentType, formatOrderPaymentRate, isOrderPaymentVisible } =
  useOrderPayments();

const isCardsVariant = computed<boolean>(() => props.variant === OrderPaymentsVariant.Cards);

const isOrderCancelled = computed<boolean>(() => props.orderStatus === OrderStatus.cancelled);

const activePayments = computed(() => payments.value.filter((payment) => payment.isActive));

const visiblePayments = computed(() => {
  return payments.value.filter((payment) => isOrderPaymentVisible(payment, props.orderStatus));
});

const mandatoryType = computed(() => getMandatoryOrderPaymentType(props.orderStatus));

const sortedVisiblePayments = computed(() => {
  return [...visiblePayments.value].sort(
    (a, b) => Number(b.type === mandatoryType.value) - Number(a.type === mandatoryType.value),
  );
});

const totalRate = computed(() => {
  return activePayments.value.reduce((total, payment) => (payment.isDeleted ? total : total + (payment.rate ?? 0)), 0);
});

const formattedTotalRate = computed<string>(() => formatOrderPaymentRate(totalRate.value));

function isMandatoryPayment(payment: OrderPayment): boolean {
  return payment.type === mandatoryType.value;
}

function isRestoreVisible(payment: OrderPayment): boolean {
  return props.canRestore && !props.readonly && Boolean(payment.isDeleted);
}

function isDeleteVisible(payment: OrderPayment): boolean {
  return !props.readonly && !payment.isDeleted && !isMandatoryPayment(payment);
}

function updatePayment(target: OrderPayment, data: Partial<OrderPayment>) {
  payments.value = payments.value.map((payment) => {
    if (isSameEntity(payment, target)) {
      return {
        ...payment,
        ...data,
      };
    }

    return payment;
  });
}

function addPayment() {
  payments.value = [...payments.value, createOrderPaymentDraft()];
}

function deletePayment(target: OrderPayment) {
  if (target.id) {
    updatePayment(target, { isDeleted: true });
    return;
  }

  payments.value = payments.value.filter((payment) => !isSameEntity(payment, target));
}

function restorePayment(target: OrderPayment) {
  updatePayment(target, { isDeleted: false });
}
</script>

<template>
  <div v-if="isCardsVariant" class="d-flex flex-column row-gap-3 order-payments-cards">
    <span class="order-payments-cards__title">{{ t("payments.order-payments") }}</span>

    <div
      v-for="(payment, index) of sortedVisiblePayments"
      :key="getEntityKey(payment)"
      class="d-flex flex-column row-gap-3 pa-4 rounded-lg order-payments-cards__item"
      data-qa="order-payment-block"
    >
      <div class="d-flex align-center justify-space-between col-gap-4">
        <div class="d-flex align-center col-gap-4">
          <span class="order-payments-cards__number">{{ t("general.payment") }} #{{ index + 1 }}</span>

          <deleted-chip v-if="payment.isDeleted" />

          <order-payment-status v-else-if="payment.status" :status="payment.status" />
        </div>

        <v-btn
          v-if="isRestoreVisible(payment)"
          :disabled="isOrderCancelled"
          class="text-uppercase font-weight-600 font-size-12"
          color="primary"
          data-qa="order-payment-restore"
          size="small"
          variant="text"
          @click="restorePayment(payment)"
        >
          {{ t("payments.restore-payment") }}
        </v-btn>

        <v-btn
          v-else-if="isDeleteVisible(payment)"
          :disabled="isOrderCancelled || !payment.isActive"
          class="text-uppercase font-weight-600 font-size-12"
          color="danger"
          data-qa="order-payment-delete"
          size="small"
          variant="text"
          @click="deletePayment(payment)"
        >
          <template #prepend>
            <v-icon icon="mdi-delete-outline" size="16" />
          </template>
          {{ t("payments.delete-payment") }}
        </v-btn>
      </div>

      <div class="d-flex flex-column row-gap-4">
        <order-payment-item
          :model-value="payment"
          :order-status="props.orderStatus"
          :readonly="props.readonly"
          class="order-payments-cards__fields"
          hide-delete
          @update:model-value="updatePayment(payment, $event)"
        />

        <slot v-if="isMandatoryPayment(payment)" name="mandatory-payment" />
      </div>
    </div>

    <div class="d-flex align-center justify-space-between col-gap-3 pr-4">
      <add-order-payment-button :disabled="props.readonly" :order-status="props.orderStatus" @click="addPayment" />

      <div class="d-flex align-center col-gap-3 order-payments-cards__total">
        <span>{{ t("payments.total-invoice-rate") }}:</span>
        <span data-qa="order-payments-total">{{ formattedTotalRate }}</span>
      </div>
    </div>
  </div>

  <div v-else class="order-payments pa-4 rounded-lg">
    <div class="text-grey-darken-1 font-weight-500 order-payments__title mb-5">{{ t("general.payments") }}</div>

    <div class="d-flex flex-column row-gap-4">
      <div v-for="payment of sortedVisiblePayments" :key="getEntityKey(payment)" class="d-flex flex-column row-gap-4">
        <order-payment-item
          :can-restore="props.canRestore"
          :model-value="payment"
          :order-status="props.orderStatus"
          :readonly="props.readonly"
          @delete="deletePayment(payment)"
          @update:model-value="updatePayment(payment, $event)"
        />

        <slot v-if="isMandatoryPayment(payment)" name="mandatory-payment" />
      </div>
    </div>

    <div class="d-flex align-center justify-space-between mt-4">
      <add-order-payment-button :disabled="props.readonly" :order-status="props.orderStatus" @click="addPayment" />

      <div class="d-flex align-center col-gap-3 pt-1 font-weight-600 text-grey-darken-3 order-payments__total">
        <span>{{ t("payments.total-invoice-rate") }}:</span>
        <span data-qa="order-payments-total">{{ formattedTotalRate }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.order-payments {
  border: 1px solid $grey-light;

  &__title,
  &__total {
    font-size: 13px;
    line-height: 18px;
  }
}

.order-payments-cards {
  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $grey-navy;
  }

  &__item {
    border: 1px solid $grey-lighter;
    background-color: $grey-lighten-5;
  }

  &__number {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $grey;
  }

  &__fields {
    &:deep(.order-payment__type),
    &:deep(.order-payment__rate) {
      flex: 1 1 0;
      min-width: 0;
    }

    &:deep(.v-field) {
      background-color: $white;
    }
  }

  &__total {
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: $grey-navy;
  }
}
</style>
